import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CircularProgress, Box } from '@mui/material';
import { FC, TouchEvent, useCallback, useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import RecipientNotification from '@components/RecipientNotification';
import { PieceAssetSize, Workflow, PieceAssetType } from '@rdTypes/apiContract';
import { ImagePreview } from '@uiKit/ImagePreview';
import { getPieceAsset } from 'src/utils/piece';

import { CaptureId, CapturePiece, DrawerTypes } from '../../types';
import sx from './styles';

type Props = {
  pieces: CapturePiece[];
  onChangeSlide: (slide: CaptureId) => void;
  cursor: CaptureId;
  onClose: () => void;
  setDrawer: (type: DrawerTypes) => void;
  workflow?: Workflow;
};

const PieceSlider: FC<Props> = ({ pieces, onChangeSlide, cursor, onClose, setDrawer, workflow }) => {
  const slideLengthRef = useRef(pieces.length);
  const touchStartX = useRef<number | null>(null);
  const [activeSlide, setActiveSlide] = useState(
    cursor ? pieces.findIndex(({ id }) => id === cursor) : pieces.length - 1,
  );

  if (!pieces.length && onClose) {
    onClose();
  }

  if (slideLengthRef.current !== pieces.length) {
    const index = pieces.findIndex(({ id }) => id === cursor);
    if (index > -1) {
      setActiveSlide(index);
    }
    slideLengthRef.current = pieces.length;
  }

  const onArrowClick = useCallback(
    (nextSlide: number) => {
      if (nextSlide > pieces.length - 1 || nextSlide < 0) return;
      setActiveSlide(nextSlide);
      onChangeSlide(pieces[nextSlide].id);
    },
    [onChangeSlide, pieces],
  );

  if (pieces.length < activeSlide + 1) {
    setActiveSlide(pieces.length - 1);
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!['ArrowRight', 'ArrowLeft'].includes(event.code)) return;
      event.preventDefault();
      const index = pieces.findIndex(({ id }) => id === cursor);
      if (event.code === 'ArrowRight') {
        const nextIndex = index + 1;
        onArrowClick(nextIndex);
      }
      if (event.code === 'ArrowLeft') {
        const prevIndex = index - 1;
        onArrowClick(prevIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [pieces, cursor, onArrowClick]);

  const recipient = pieces[activeSlide]?.piece?.recipient || pieces[activeSlide]?.pieceDetection?.recipient;

  return (
    <Box sx={sx.container}>
      {activeSlide > 0 && (
        <Box sx={{ ...sx.arrows, left: '20px' }} onClick={() => onArrowClick(activeSlide - 1)}>
          <ChevronLeft />
        </Box>
      )}
      {activeSlide < pieces.length - 1 && (
        <Box sx={{ ...sx.arrows, right: '20px' }} onClick={() => onArrowClick(activeSlide + 1)}>
          <ChevronRight />
        </Box>
      )}
      {recipient && <RecipientNotification recipient={recipient} capturePiece={pieces[activeSlide]} />}
      <Box sx={sx.sliderContainer(activeSlide)}>
        {pieces.map((piece, index) => {
        const asset = getPieceAsset(piece?.piece?.pieceAssets || [], PieceAssetSize.Large);
        return (
          <Box
            key={piece.id}
            sx={sx.itemContainer}
            onTouchStart={(event: TouchEvent) => (touchStartX.current = event.touches[0].clientX)}
            onTouchEnd={(event: TouchEvent) => {
              if (!touchStartX.current) return;
              const distance = touchStartX.current - event.changedTouches[0].clientX;
              if (distance > 40 && activeSlide < pieces.length - 1) {
                onArrowClick(activeSlide + 1);
              } else if (distance < -40 && activeSlide > 0) {
                onArrowClick(activeSlide - 1);
              }
            }}
          >
            {Math.abs(activeSlide - index) <= 5 && (
              <>
                {!piece?.piece && (
                  <Box sx={sx.loaderBackground}>
                    <CircularProgress size={80} />
                  </Box>
                )}

                <AutoSizer>
                  {({ height, width }) => (
                    <ImagePreview
                      imageDataUrl={
                        piece.editImage ||
                        asset?.url ||
                        (piece.photo && (piece.photo instanceof File ? URL.createObjectURL(piece.photo) : piece.photo))
                      }
                      croppingBox={asset?.croppingBox}
                      height={height}
                      width={width}
                      isDrawAnnotations={!!asset?.croppingBox || asset?.pieceAssetType !== PieceAssetType.Cropped}
                      onAnnotationClick={() => setDrawer(DrawerTypes.Barcode)}
                      annotations={{
                        workflow: workflow,
                        items: piece.pieceAnnotations,
                      }}
                    />
                  )}
                </AutoSizer>
              </>
            )}
          </Box>
        );})}
      </Box>
    </Box>
  );
};

export default PieceSlider;
